import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { AdminAction, AdminModuleId } from '../services/firebaseFunctions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Exige apenas "administrador ativo", sem módulo específico — usado pela landing (`/`), que só
   * decide para onde navegar depois que o gate binário passa. Ignorado quando `module` é
   * informado (a checagem de módulo já implica administrador ativo).
   */
  requireAdmin?: boolean;
  /** Módulo do menu a checar (`.agents/memory/contracts.md` §"Vocabulário compartilhado"). */
  module?: AdminModuleId;
  /** Ação mínima exigida no módulo — `'ver'` é satisfeita por `'ver'` OU `'editar'`. */
  action?: AdminAction;
}

const CenteredMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" gap={2} p={3}>
    {children}
  </Box>
);

/**
 * Controle de acesso do painel — RBAC (REEDY-17).
 *
 * Substitui a checagem binária `requireAdmin` por checagem de módulo/ação, e distingue três
 * desfechos de negação que NÃO podem parecer a mesma coisa (jornada J3, risco S-14):
 *  - sem permissão (`adminAccessStatus === 'denied'`, cobre não-admin e admin desativado);
 *  - indisponível (`adminAccessStatus === 'unavailable'`, falha de transporte/backend);
 *  - módulo/ação insuficiente (administrador ativo, mas sem a permissão exigida na rota).
 * Nenhum dos três produz tela branca, spinner infinito ou loop de redirecionamento: os dois
 * primeiros são desfechos terminais de `AuthContext` (nunca ficam presos em "carregando"); o
 * terceiro é avaliado só depois que `adminAccessStatus` chega a um estado terminal.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  module,
  action = 'ver',
}) => {
  const { isAuthenticated, loading, adminAccessStatus, hasPermission, refreshAdminAccess } = useAuth();

  if (loading) {
    return (
      <CenteredMessage>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verificando autenticação...
        </Typography>
      </CenteredMessage>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const requiresAdminCheck = requireAdmin || Boolean(module);

  if (requiresAdminCheck && adminAccessStatus === 'loading') {
    return (
      <CenteredMessage>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verificando permissões...
        </Typography>
      </CenteredMessage>
    );
  }

  if (requiresAdminCheck && adminAccessStatus === 'unavailable') {
    return (
      <CenteredMessage>
        <Typography variant="h5" color="text.primary" gutterBottom data-testid="access-unavailable">
          Não foi possível verificar suas permissões
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Houve uma falha de comunicação com o servidor. Isso não significa que o acesso foi
          negado — tente novamente em instantes.
        </Typography>
        <Button variant="contained" onClick={() => void refreshAdminAccess()}>
          Tentar novamente
        </Button>
      </CenteredMessage>
    );
  }

  if (requiresAdminCheck && adminAccessStatus === 'denied') {
    return (
      <CenteredMessage>
        <Typography variant="h4" color="error" gutterBottom data-testid="access-denied">
          Acesso Negado
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Você não tem permissão para acessar esta página.
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Apenas administradores ativos podem acessar esta área.
        </Typography>
      </CenteredMessage>
    );
  }

  if (module && !hasPermission(module, action)) {
    return (
      <CenteredMessage>
        <Typography variant="h4" color="error" gutterBottom data-testid="access-denied">
          Acesso Negado
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Seu perfil de administrador não tem permissão para acessar este módulo.
        </Typography>
      </CenteredMessage>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
